import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import { PrimaryButton } from '../components/PrimaryButton';
import { SecondaryButton } from '../components/SecondaryButton';
import {
  confirmBookingCheckout,
  confirmSubscriptionCheckout,
  type BookingConfirmResponse,
} from '../../lib/booking-api';
import {
  clearBookingDraft,
  getBookingDraft,
} from '../../lib/booking-draft';
import { customerApiErrorMessage } from '../../lib/customer-api';
import { trackPurchase } from '../../lib/analytics';

type State =
  | { kind: 'loading' }
  | {
      kind: 'oneoff';
      info: BookingConfirmResponse;
    }
  | {
      kind: 'subscription';
      info: {
        id: string;
        subscription_status: string;
        amount_per_cycle: number;
        currency: string;
        frequency: string;
        next_billing_at: string | null;
      };
    }
  | { kind: 'error'; message: string };

/**
 * Landing page Stripe redirects the customer back to after a
 * successful Checkout. The session id is in the URL; we hit the
 * backend's idempotent confirm endpoint to flip status to CONFIRMED
 * (or read the already-finalised row if the webhook beat us here),
 * then show a confirmation screen.
 *
 * Knows whether to call the one-off or subscription confirm endpoint
 * from the draft's `mode` — the draft is still in localStorage at
 * this point (Stripe redirected to the same browser).
 */
export default function BookingConfirmationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id') ?? '';
  // Mode can come from the query (?mode=subscription) or be implied by
  // the route path: the backend routes Stripe back to
  // /booking/subscription-confirmation for recurring checkouts and
  // /booking/confirmation for one-offs.
  const pathHintsSubscription = location.pathname.includes(
    'subscription-confirmation',
  );
  const modeOverride = params.get('mode') ?? (pathHintsSubscription ? 'subscription' : '');

  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    if (!sessionId) {
      setState({
        kind: 'error',
        message: 'Missing session_id from the payment redirect.',
      });
      return;
    }
    (async () => {
      const draft = getBookingDraft();
      // Prefer an explicit ?mode= override (Stripe success URLs
      // can carry the hint) before falling back to the draft. If
      // neither is set, default to one-off.
      const hinted: 'SUBSCRIPTION' | 'ONE_OFF' =
        modeOverride === 'subscription'
          ? 'SUBSCRIPTION'
          : modeOverride === 'oneoff'
            ? 'ONE_OFF'
            : draft?.mode === 'SUBSCRIPTION'
              ? 'SUBSCRIPTION'
              : 'ONE_OFF';

      // The draft mode can be stale (e.g. customer flipped between
      // recurring + one-off mid-wizard) — Stripe doesn't tell us which
      // checkout this was. Try the hinted endpoint first, then fall
      // back to the other one if it 404s. Only error if both miss.
      // Booked service ids for the Purchase payload — read BEFORE the draft
      // is cleared below.
      const contentIds = Array.isArray((draft as any)?.items)
        ? ((draft as any).items as any[])
            .map((i) => i?.service_id)
            .filter((id): id is string => !!id)
        : [];

      const tryOneOff = async () => {
        const res = await confirmBookingCheckout(sessionId);
        clearBookingDraft();
        setState({ kind: 'oneoff', info: res });
        // Conversion event. Deduped on sessionId inside trackPurchase, so a
        // refresh of this page can't double-count the sale.
        trackPurchase({
          value: Number((res as any)?.total_amount) || 0,
          currency: (res as any)?.currency ?? 'GBP',
          transactionId: sessionId,
          contentIds,
        });
      };
      const trySubscription = async () => {
        const res = await confirmSubscriptionCheckout(sessionId);
        clearBookingDraft();
        setState({ kind: 'subscription', info: res });
        // Recurring: report the first cycle's charge as the purchase value.
        trackPurchase({
          value: Number((res as any)?.amount_per_cycle) || 0,
          currency: (res as any)?.currency ?? 'GBP',
          transactionId: sessionId,
          contentIds,
        });
      };

      const primary = hinted === 'SUBSCRIPTION' ? trySubscription : tryOneOff;
      const fallback = hinted === 'SUBSCRIPTION' ? tryOneOff : trySubscription;

      try {
        await primary();
      } catch (primaryErr) {
        try {
          await fallback();
        } catch {
          // Surface the original error — usually the more specific one
          // for the mode the customer actually paid for.
          setState({
            kind: 'error',
            message: customerApiErrorMessage(
              primaryErr,
              "We couldn't load your booking. Please refresh.",
            ),
          });
        }
      }
    })();
  }, [sessionId, modeOverride]);

  return (
    <div className="min-h-screen bg-[#fcfcfc] flex flex-col">
      <MainHeader />

      <main className="flex-1 max-w-[720px] mx-auto px-4 md:px-8 py-16 w-full">
        {state.kind === 'loading' ? (
          <div className="flex flex-col items-center text-center">
            <span className="material-icons text-[#8e2a2f] text-[48px] animate-spin">
              autorenew
            </span>
            <p className="mt-4 font-['DM_Sans',sans-serif] text-[#3a3335]">
              Finalising your booking…
            </p>
          </div>
        ) : state.kind === 'error' ? (
          <div className="flex flex-col items-center text-center">
            <span className="material-icons text-red-600 text-[48px]">
              error_outline
            </span>
            <h1 className="mt-4 font-['Playfair_Display',sans-serif] font-extrabold text-[#3a3335] text-[28px]">
              Something went wrong
            </h1>
            <p className="mt-2 font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] max-w-md">
              {state.message}
            </p>
            <PrimaryButton
              className="mt-8"
              onClick={() => navigate('/bookings')}
            >
              View my bookings
            </PrimaryButton>
          </div>
        ) : state.kind === 'oneoff' ? (
          <div className="flex flex-col items-center text-center">
            <SuccessGlyph />
            <h1 className="mt-6 font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[36px] leading-[44px]">
              Thank you!
            </h1>
            <p className="mt-4 font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px]">
              Booking <span className="font-bold">{state.info.reference}</span>{' '}
              confirmed.
            </p>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px] mt-1">
              {new Date(state.info.scheduled_start).toLocaleString(undefined, {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                hour: 'numeric',
                minute: '2-digit',
              })}
            </p>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px] mt-1">
              Total {state.info.currency}{' '}
              {Number(state.info.total_amount).toFixed(2)}
            </p>
            <div className="mt-8 flex gap-3">
              <PrimaryButton onClick={() => navigate(`/bookings/${state.info.id}`)}>
                See your booking
              </PrimaryButton>
              <SecondaryButton onClick={() => navigate('/bookings')}>
                All bookings
              </SecondaryButton>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center text-center">
            <SuccessGlyph />
            <h1 className="mt-6 font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[36px] leading-[44px]">
              Subscription active
            </h1>
            <p className="mt-4 font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px]">
              Your {state.info.frequency.toLowerCase()} booking is set up.
            </p>
            {state.info.next_billing_at ? (
              <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px] mt-1">
                Next visit billed on{' '}
                {new Date(state.info.next_billing_at).toLocaleDateString(
                  undefined,
                  { day: 'numeric', month: 'long', year: 'numeric' },
                )}
              </p>
            ) : null}
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px] mt-1">
              {state.info.currency}{' '}
              {Number(state.info.amount_per_cycle).toFixed(2)} per cycle
            </p>
            <div className="mt-8 flex gap-3">
              {/* Land the customer on the just-created subscription's
                  detail page — same UX as the one-off branch above,
                  which takes them to the booking they just made. */}
              <PrimaryButton
                onClick={() => navigate(`/subscriptions/${state.info.id}`)}
              >
                See your subscription
              </PrimaryButton>
              <SecondaryButton onClick={() => navigate('/bookings')}>
                All bookings
              </SecondaryButton>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}

function SuccessGlyph() {
  return (
    <svg
      width="181"
      height="125"
      viewBox="0 0 181 125"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_conf_1)">
        <path
          d="M174.613 124C185.347 97.1844 182.1 67.2418 168.256 44.2452L23.3348 64.2955L162.215 35.3746C103.683 -37.5584 -10.2415 10.8172 0.740232 102.9L174.613 124Z"
          fill="white"
        />
        <path
          d="M174.613 124C185.347 97.1844 182.1 67.2418 168.256 44.2452L23.3348 64.2955L162.215 35.3746C103.683 -37.5584 -10.2415 10.8172 0.740232 102.9L174.613 124Z"
          fill="#3A3335"
          fillOpacity="0.04"
        />
        <circle cx="89.5" cy="88.5" r="36.5" fill="#C6D8D3" />
        <path
          d="M86.5 93.9219L98.8594 81.5078L100.5 83.1484L86.5 97.1484L79.9922 90.6406L81.5781 89L86.5 93.9219Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_conf_1">
          <rect width="181" height="125" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}
